import {
  LoginRequest,
  LoginResponse,
  UserProfileResponse,
  Project,
  ProjectDetailResponse,
  ProjectMember,
  CSVProcessRequest,
  CSVProcessResponse,
  ValidationResult,
  DataRequest,
  DataRequestCreateRequest,
  DataRequestListResponse,
  DataRequestRedirectRequest,
  RedirectTargetsResponse,
  ApiError,
  PIIValueFinding,
  TwoFAStatusResponse,
  TwoFASetupResponse,
  User,
  RegisterRequest,
  RegisterWithPIRequest,
  RegisterResponse,
  EmailVerificationRequest,
  EmailVerificationResponse,
  ResendVerificationRequest,
  DatasetsListResponse,
  AccessRequestListResponse,
  ApproveAccessRequestRequest,
  CreateInvitationRequest,
  InvitationResponse,
  InvitationListResponse,
  ValidateInvitationResponse,
  Notification,
  NotificationListResponse,
  NotificationUnreadCountResponse,
  DataSourceActivity,
  ProjectSettings,
  UpdateProjectSettingsRequest,
  SiteNotice,
  UploadInspectResponse,
  ConsoleResponse,
} from "../types/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/v1";

// Callback for handling session expiration
let onSessionExpired: (() => void) | null = null;

// The import wizard's step 2 choices, forwarded to the commit endpoints so
// the file is parsed exactly as it was previewed — see UploadInspectResponse
// and the backend's normalizeUploadToStandardCSV.
export interface UploadParseOptions {
  delimiter?: string;
  headerRow?: boolean;
  sheetName?: string; // Excel only
  // Excel only. true (default): store the full workbook.
  storeAsWorkbook?: boolean;
}

function appendUploadParseOptions(
  formData: FormData,
  opts: UploadParseOptions | undefined,
): void {
  if (!opts) return;
  if (opts.delimiter) formData.append("delimiter", opts.delimiter);
  if (opts.headerRow === false) formData.append("header_row", "false");
  if (opts.sheetName) formData.append("sheet_name", opts.sheetName);
  if (opts.storeAsWorkbook === false) {
    formData.append("excel_storage_mode", "csv");
  }
}

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem("access_token");
  }

  // Set the session expiration callback (called from App.tsx)
  setOnSessionExpired(callback: () => void) {
    onSessionExpired = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    skipContentType: boolean = false,
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Only add Content-Type if not skipContentType (for FormData, browser sets it automatically)
    if (!skipContentType) {
      headers["Content-Type"] = "application/json";
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - but only treat as session expiration if user was authenticated
      // During login/register, 401 just means invalid credentials, not session expiration
      if (response.status === 401 && this.token) {
        // Clear local token
        this.token = null;
        localStorage.removeItem("access_token");

        // Trigger session expiration callback
        if (onSessionExpired) {
          onSessionExpired();
        }

        throw new Error("Session expired. Please login again.");
      }

      const errorData: any = await response.json();
      // Handle password validation errors with requirements
      if (errorData.requirements && Array.isArray(errorData.requirements)) {
        const errorMessage = `${errorData.error || "Password does not meet requirements"}:\n- ${errorData.requirements.join("\n- ")}`;
        throw new Error(errorMessage);
      }
      throw new Error(errorData.error || "Request failed");
    }

    return response.json();
  }

  async register(
    request: RegisterRequest | RegisterWithPIRequest,
  ): Promise<RegisterResponse> {
    const response = await this.request<RegisterResponse>("/auth/register", {
      method: "POST",
      body: JSON.stringify(request),
    });
    if (response.access_token) {
      this.token = response.access_token;
      localStorage.setItem("access_token", this.token);
    }
    return response;
  }

  async verifyEmail(
    email: string,
    code: string,
  ): Promise<EmailVerificationResponse> {
    const request: EmailVerificationRequest = {
      email,
      code,
    };

    const response = await this.request<EmailVerificationResponse>(
      "/auth/verify-email",
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );

    // Clear the registration email after successful verification
    if (response.success) {
      localStorage.removeItem("registration_email");
    }

    return response;
  }

  async resendEmailVerification(
    email: string,
  ): Promise<EmailVerificationResponse> {
    const request: ResendVerificationRequest = { email };

    return this.request<EmailVerificationResponse>(
      "/auth/resend-verification",
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    // Only save token if we have a valid access token
    // Don't save if we're just getting a 2FA prompt or setup requirement
    if (response.access_token) {
      this.token = response.access_token;
      localStorage.setItem("access_token", this.token);
    }

    return response;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem("access_token");
  }

  async getPasswordPolicy(): Promise<{
    min_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_digit: boolean;
    require_special_char: boolean;
    special_chars: string;
    forbidden_patterns: string[];
  }> {
    return this.request("/auth/password-policy");
  }

  // Environment banner. Unauthenticated, so it also works on the login and public
  // pages — which is where someone most needs to be told they are on the wrong
  // server.
  async getSiteNotice(): Promise<SiteNotice> {
    return this.request("/site-notice");
  }

  async requestPasswordReset(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/auth/forgot-password",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );
  }

  async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/auth/reset-password",
      {
        method: "POST",
        body: JSON.stringify({ token, new_password: newPassword }),
      },
    );
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
    totpToken?: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      "/user/change-password",
      {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          totp_token: totpToken,
        }),
      },
    );
  }

  async getProfile(): Promise<UserProfileResponse> {
    return this.request<UserProfileResponse>("/user/profile");
  }

  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>("/user/projects");
  }

  async getProjectDetail(projectId: number): Promise<ProjectDetailResponse> {
    return this.request<ProjectDetailResponse>(`/projects/${projectId}`);
  }

  async getProjectMembers(projectId: number): Promise<ProjectMember[]> {
    return this.request<ProjectMember[]>(`/projects/${projectId}/members`);
  }

  async getProjectDataRequests(
    projectId: number,
  ): Promise<DataRequestListResponse> {
    return this.request<DataRequestListResponse>(
      `/projects/${projectId}/data-requests`,
    );
  }

  async validateCSV(request: CSVProcessRequest): Promise<ValidationResult> {
    return this.request<ValidationResult>("/csv/validate-columns", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async pseudonymizeCSV(
    request: CSVProcessRequest,
  ): Promise<CSVProcessResponse> {
    return this.request<CSVProcessResponse>("/csv/pseudonymize", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async reverseCSV(request: CSVProcessRequest): Promise<CSVProcessResponse> {
    return this.request<CSVProcessResponse>("/csv/reverse", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // Data Request methods
  async createDataRequest(
    request: DataRequestCreateRequest,
  ): Promise<DataRequest> {
    return this.request<DataRequest>("/data-requests", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async getDataRequests(): Promise<DataRequestListResponse> {
    return this.request<DataRequestListResponse>("/data-requests");
  }

  async getConsole(): Promise<ConsoleResponse> {
    return this.request<ConsoleResponse>("/console");
  }

  async getDataRequest(id: number): Promise<DataRequest> {
    // Currently unused
    return this.request<DataRequest>(`/data-requests/${id}`);
  }

  async updateDataRequest(
    id: number,
    request: DataRequestCreateRequest,
  ): Promise<DataRequest> {
    return this.request<DataRequest>(`/data-requests/${id}`, {
      method: "PUT",
      body: JSON.stringify(request),
    });
  }

  async getRequestComments(requestId: number): Promise<{ comments: any[] }> {
    return this.request<{ comments: any[] }>(
      `/data-requests/${requestId}/comments`,
    );
  }

  async addRequestComment(
    requestId: number,
    comment: string,
  ): Promise<{ message: string; comment: any }> {
    return this.request<{ message: string; comment: any }>(
      `/data-requests/${requestId}/comments`,
      {
        method: "POST",
        body: JSON.stringify({ comment }),
      },
    );
  }

  // Dataset methods
  async getDatasets(
    visibilityView?: string,
    projectId?: number,
  ): Promise<DatasetsListResponse> {
    const params = new URLSearchParams();
    if (visibilityView) params.set("visibility_view", visibilityView);
    if (projectId) params.set("project_id", String(projectId));
    const qs = params.toString();
    return this.request<DatasetsListResponse>(`/datasets${qs ? `?${qs}` : ""}`);
  }

  async downloadDatasetPseudonymized(
    datasetId: number,
    projectId: number,
    dataServiceId?: number,
    // Only meaningful for a dataset stored as an Excel workbook: "csv" asks
    // for a flattened, first-sheet CSV view instead of the full workbook.
    format?: "native" | "csv",
  ): Promise<Blob> {
    const params = new URLSearchParams({ project_id: String(projectId) });
    if (dataServiceId) params.set("data_service_id", String(dataServiceId));
    if (format === "csv") params.set("format", "csv");
    const url = `${API_BASE_URL}/datasets/${datasetId}/download?${params}`;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errData = await response
        .json()
        .catch(() => ({ error: "Download failed" }));
      throw new Error(errData.error || "Download failed");
    }
    return response.blob();
  }

  // bulkDownloadDatasets fetches several pseudonymized datasets for one project as a
  // single ZIP archive (also containing generated loader files). The server re-checks
  // download permission per dataset and fails closed if any is not allowed.
  async bulkDownloadDatasets(
    projectId: number,
    datasetIds: number[],
  ): Promise<Blob> {
    const url = `${API_BASE_URL}/datasets/bulk-download`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ project_id: projectId, dataset_ids: datasetIds }),
    });
    if (!response.ok) {
      const errData = await response
        .json()
        .catch(() => ({ error: "Download failed" }));
      throw new Error(errData.error || "Download failed");
    }
    return response.blob();
  }

  // Admin methods
  async getAdminDataRequests(): Promise<DataRequestListResponse> {
    return this.request<DataRequestListResponse>("/admin/data-requests");
  }

  async getAdminDataRequest(id: number): Promise<DataRequest> {
    return this.request<DataRequest>(`/admin/data-requests/${id}`);
  }

  async reviewDataRequest(
    requestId: number,
    review: {
      status: string;
      admin_notes: string;
      file_access_mode?: string;
      file_access_users?: number[];
    },
  ): Promise<DataRequest> {
    return this.request<DataRequest>(
      `/admin/data-requests/${requestId}/review`,
      {
        method: "POST",
        body: JSON.stringify(review),
      },
    );
  }

  async redirectDataRequest(
    requestId: number,
    req: DataRequestRedirectRequest,
  ): Promise<DataRequest> {
    return this.request<DataRequest>(
      `/admin/data-requests/${requestId}/redirect`,
      { method: "POST", body: JSON.stringify(req) },
    );
  }

  async getRedirectTargets(): Promise<RedirectTargetsResponse> {
    return this.request<RedirectTargetsResponse>("/admin/redirect-targets");
  }

  async getDataRequestSources(requestId: number): Promise<{
    sources: import("../types/api").DataRequestSource[];
    total: number;
  }> {
    return this.request(`/admin/data-requests/${requestId}/sources`);
  }

  async uploadDataFile(
    requestId: number,
    file: File,
  ): Promise<{ message: string; filename: string }> {
    const formData = new FormData();
    formData.append("data_file", file);

    const url = `${API_BASE_URL}/admin/data-requests/${requestId}/upload`;

    const headers: Record<string, string> = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.error || "Upload failed");
    }

    return response.json();
  }

  async previewDataFile(
    requestId: number,
    file: File,
  ): Promise<{
    filename: string;
    available_columns: string[];
    requested_fields: string[];
    request_id: number;
    suspected_pii?: { column: string; reasons: string[] }[];
    pii_value_findings?: PIIValueFinding[];
  }> {
    const formData = new FormData();
    formData.append("data_file", file);

    const url = `${API_BASE_URL}/admin/data-requests/${requestId}/preview`;

    const headers: Record<string, string> = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.error || "Preview failed");
    }

    return response.json();
  }

  async uploadDataFileWithColumns(
    requestId: number,
    file: File,
    selectedColumns: string[],
    metadataScope: string = "projects",
    metadataProjectIds: number[] = [],
    downloadScope: string = "none",
    downloadUserIds: number[] = [],
    datasetExpiry?: string,
    datasetTitle?: string,
    piiFindingsAcknowledged: boolean = false,
    piiValueRedactions: PIIValueFinding[] = [],
    piiDroppedColumns: string[] = [],
    parseOptions?: UploadParseOptions,
  ): Promise<{
    message: string;
    filename: string;
  }> {
    const formData = new FormData();
    formData.append("data_file", file);
    formData.append("selected_columns", JSON.stringify(selectedColumns));
    formData.append("metadata_scope", metadataScope);
    if (metadataProjectIds.length > 0) {
      formData.append(
        "metadata_project_ids",
        JSON.stringify(metadataProjectIds),
      );
    }
    formData.append("download_scope", downloadScope);
    if (downloadUserIds.length > 0) {
      formData.append("download_user_ids", JSON.stringify(downloadUserIds));
    }
    if (datasetExpiry) {
      formData.append("dataset_expiry", datasetExpiry);
    }
    if (datasetTitle) {
      formData.append("dataset_title", datasetTitle);
    }
    if (piiFindingsAcknowledged) {
      formData.append("pii_findings_acknowledged", "true");
    }
    if (piiValueRedactions.length > 0) {
      formData.append(
        "pii_value_redactions",
        JSON.stringify(piiValueRedactions),
      );
    }
    if (piiDroppedColumns.length > 0) {
      formData.append("pii_dropped_columns", JSON.stringify(piiDroppedColumns));
    }
    appendUploadParseOptions(formData, parseOptions);

    const url = `${API_BASE_URL}/admin/data-requests/${requestId}/upload`;

    const headers: Record<string, string> = {};
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData: {
        error?: string;
        pii_value_findings?: PIIValueFinding[];
      } = await response.json();
      const err = new Error(errorData.error || "Upload failed") as Error & {
        piiValueFindings?: PIIValueFinding[];
      };
      if (errorData.pii_value_findings) {
        err.piiValueFindings = errorData.pii_value_findings;
      }
      throw err;
    }

    return response.json();
  }

  async getUploadConfig(): Promise<{
    retention_days: number;
    max_file_size_mb: number;
  }> {
    return this.request<{ retention_days: number; max_file_size_mb: number }>(
      "/config/upload",
    );
  }

  // 2FA methods
  async getTwoFAStatus(): Promise<TwoFAStatusResponse> {
    return this.request<TwoFAStatusResponse>("/auth/2fa/status");
  }

  async setupTwoFA(password: string): Promise<TwoFASetupResponse> {
    return this.request<TwoFASetupResponse>("/auth/2fa/setup", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  }

  async enableTwoFA(
    totpToken: string,
    secret: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>("/auth/2fa/enable", {
      method: "POST",
      body: JSON.stringify({ totp_token: totpToken, secret }),
    });
  }

  // Admin 2FA methods
  async adminDisableTwoFA(userId: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/admin/users/${userId}/disable-2fa`,
      {
        method: "POST",
      },
    );
  }

  async adminPromotePI(userId: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/admin/users/${userId}/promote-pi`,
      { method: "POST" },
    );
  }

  async adminRevokePI(userId: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/admin/users/${userId}/revoke-pi`,
      { method: "POST" },
    );
  }

  // Admin Dashboard methods
  async getAdminDashboardOverview(): Promise<
    import("../types/api").AdminDashboardStats
  > {
    return this.request<import("../types/api").AdminDashboardStats>(
      "/admin/dashboard/overview",
    );
  }

  async adminGetUsers(): Promise<User[]> {
    return this.request<User[]>("/admin/users");
  }

  async adminGetProjects(): Promise<Project[]> {
    return this.request<Project[]>("/admin/projects");
  }

  async adminGetUsersWithProjects(): Promise<
    import("../types/api").UserWithProjects[]
  > {
    return this.request<import("../types/api").UserWithProjects[]>(
      "/admin/users-with-projects",
    );
  }

  async adminGetProjectsOverview(
    includeArchived = false,
  ): Promise<import("../types/api").ProjectOverview[]> {
    return this.request<import("../types/api").ProjectOverview[]>(
      `/admin/projects/overview${includeArchived ? "?include_archived=true" : ""}`,
    );
  }

  async adminSetProjectArchived(
    projectId: number,
    archived: boolean,
  ): Promise<Project> {
    return this.request<Project>(`/admin/projects/${projectId}/archive`, {
      method: archived ? "POST" : "DELETE",
    });
  }

  // Project detail and per-project data requests use the shared, role-aware endpoints
  // getProjectDetail() / getProjectDataRequests() for every role — there is no separate
  // admin variant (the old ones leaked any project to any PI).

  async adminAssignUserToProject(
    projectId: number,
    userId: number,
    role: string = "researcher",
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/admin/projects/${projectId}/assign-user`,
      {
        method: "POST",
        body: JSON.stringify({ user_id: userId, role }),
      },
    );
  }

  async adminRemoveUserFromProject(
    projectId: number,
    userId: number,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/admin/projects/${projectId}/remove-users/${userId}`,
      {
        method: "DELETE",
      },
    );
  }

  // Note: User creation via admin API is not implemented
  // Users should use the self-service registration at /register
  async adminCreateUser(_request: {
    email: string;
    name: string;
    institution: string;
    password: string;
    role?: string; // "researcher" | "pi" | "manager" | "data_uploader"
  }): Promise<{ message: string; user_id: number }> {
    throw new Error(
      "Admin user creation via API is not available. Users should register via the self-service registration page.",
    );
  }

  async adminCreateProject(request: {
    title: string;
    description: string;
  }): Promise<Project> {
    return this.request<Project>("/admin/project/create", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async adminUpdateProject(
    projectId: number,
    request: {
      title: string;
      description: string;
    },
  ): Promise<Project> {
    return this.request<Project>(`/admin/projects/${projectId}`, {
      method: "PUT",
      body: JSON.stringify(request),
    });
  }

  async adminSetDatasetVisibility(
    projectId: number,
    datasetId: number,
    isHidden: boolean,
  ): Promise<{ is_hidden: boolean }> {
    return this.request<{ is_hidden: boolean }>(
      `/admin/projects/${projectId}/datasets/${datasetId}/visibility`,
      {
        method: "PUT",
        body: JSON.stringify({ is_hidden: isHidden }),
      },
    );
  }

  async getProjectSettings(projectId: number): Promise<ProjectSettings> {
    return this.request<ProjectSettings>(
      `/admin/projects/${projectId}/settings`,
    );
  }

  async updateProjectSettings(
    projectId: number,
    request: UpdateProjectSettingsRequest,
  ): Promise<ProjectSettings> {
    return this.request<ProjectSettings>(
      `/admin/projects/${projectId}/settings`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      },
    );
  }

  // Import wizard step 2/4: drives the parse-settings preview (delimiter/
  // sheet/header-row) and, when pseudonymizeColumns/dropColumns are passed,
  // the step 4 de-identified sample preview. `route` is one of the four
  // role-scoped inspect endpoints (admin/self/data-source/admin-data-request)
  // — the same shape response either way.
  async uploadInspect(
    route: string,
    file: File,
    opts?: UploadParseOptions & {
      pseudonymizeColumns?: string[];
      dropColumns?: string[];
    },
  ): Promise<UploadInspectResponse> {
    const formData = new FormData();
    formData.append("file", file);
    appendUploadParseOptions(formData, opts);
    if (opts?.pseudonymizeColumns?.length) {
      formData.append(
        "pseudonymize_columns",
        JSON.stringify(opts.pseudonymizeColumns),
      );
    }
    if (opts?.dropColumns?.length) {
      formData.append("drop_columns", JSON.stringify(opts.dropColumns));
    }
    return this.request<UploadInspectResponse>(
      route,
      { method: "POST", body: formData },
      true,
    );
  }

  async adminPreviewProjectFile(
    projectId: number,
    file: File,
  ): Promise<{
    available_columns: string[];
    suspected_pii?: { column: string; reasons: string[] }[];
    pii_value_findings?: PIIValueFinding[];
    project_id: number;
    filename: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.request<{
      available_columns: string[];
      suspected_pii?: { column: string; reasons: string[] }[];
      pii_value_findings?: PIIValueFinding[];
      project_id: number;
      filename: string;
    }>(
      `/admin/projects/${projectId}/preview-file`,
      {
        method: "POST",
        body: formData,
      },
      true, // skipContentType to let browser set multipart boundary
    );
  }

  async adminUploadProjectFile(
    projectId: number,
    file: File,
    columns: string[],
    notes: string,
    metadataScope: string,
    downloadScope: string,
    downloadUserIds: number[],
    displayName?: string,
    piiFindingsAcknowledged: boolean = false,
    piiValueRedactions: PIIValueFinding[] = [],
    piiDroppedColumns: string[] = [],
    parseOptions?: UploadParseOptions,
  ): Promise<{ message: string; file: any; project_id: number }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("columns", JSON.stringify(columns));
    formData.append("notes", notes);
    formData.append("metadata_scope", metadataScope);
    formData.append("download_scope", downloadScope);
    if (downloadScope === "selected_members" && downloadUserIds.length > 0) {
      formData.append("download_user_ids", JSON.stringify(downloadUserIds));
    }
    if (displayName) formData.append("display_name", displayName);
    if (piiFindingsAcknowledged)
      formData.append("pii_findings_acknowledged", "true");
    if (piiValueRedactions.length > 0) {
      formData.append(
        "pii_value_redactions",
        JSON.stringify(piiValueRedactions),
      );
    }
    if (piiDroppedColumns.length > 0) {
      formData.append("pii_dropped_columns", JSON.stringify(piiDroppedColumns));
    }
    appendUploadParseOptions(formData, parseOptions);

    // Use raw fetch so we can surface PII findings from a 422 response,
    // mirroring uploadDataFileWithColumns. request() would swallow the body.
    const url = `${API_BASE_URL}/admin/projects/${projectId}/upload-file`;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const res = await fetch(url, { method: "POST", headers, body: formData });
    if (!res.ok) {
      const body: { error?: string; pii_value_findings?: PIIValueFinding[] } =
        await res.json().catch(() => ({}));
      const err = new Error(body.error || "Upload failed") as Error & {
        piiValueFindings?: PIIValueFinding[];
      };
      if (body.pii_value_findings)
        err.piiValueFindings = body.pii_value_findings;
      throw err;
    }
    return res.json();
  }

  async previewProjectFile(
    projectId: number,
    file: File,
  ): Promise<{
    available_columns: string[];
    suspected_pii?: { column: string; reasons: string[] }[];
    pii_value_findings?: PIIValueFinding[];
    project_id: number;
    filename: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    return this.request<{
      available_columns: string[];
      suspected_pii?: { column: string; reasons: string[] }[];
      pii_value_findings?: PIIValueFinding[];
      project_id: number;
      filename: string;
    }>(
      `/projects/${projectId}/self-preview`,
      { method: "POST", body: formData },
      true,
    );
  }

  async previewDataSourceUpload(
    requestId: number,
    file: File,
  ): Promise<{
    filename: string;
    available_columns: string[];
    suspected_pii?: { column: string; reasons: string[] }[];
    pii_value_findings?: PIIValueFinding[];
    request_id: number;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    return this.request<{
      filename: string;
      available_columns: string[];
      suspected_pii?: { column: string; reasons: string[] }[];
      pii_value_findings?: PIIValueFinding[];
      request_id: number;
    }>(
      `/data-source/accepted/${requestId}/preview`,
      { method: "POST", body: formData },
      true,
    );
  }

  async selfUploadProjectFile(
    projectId: number,
    file: File,
    columns: string[],
    metadataScope: string,
    downloadScope: string,
    downloadUserIds: number[],
    notes?: string,
    displayName?: string,
    piiFindingsAcknowledged: boolean = false,
    piiValueRedactions: PIIValueFinding[] = [],
    piiDroppedColumns: string[] = [],
    parseOptions?: UploadParseOptions,
  ): Promise<{
    message: string;
    dataset_id: number;
    project_id: number;
    row_count: number;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("columns", JSON.stringify(columns));
    formData.append("metadata_scope", metadataScope);
    formData.append("download_scope", downloadScope);
    formData.append("download_user_ids", JSON.stringify(downloadUserIds));
    if (notes) formData.append("notes", notes);
    if (displayName) formData.append("display_name", displayName);
    if (piiFindingsAcknowledged)
      formData.append("pii_findings_acknowledged", "true");
    if (piiValueRedactions.length > 0) {
      formData.append(
        "pii_value_redactions",
        JSON.stringify(piiValueRedactions),
      );
    }
    if (piiDroppedColumns.length > 0) {
      formData.append("pii_dropped_columns", JSON.stringify(piiDroppedColumns));
    }
    appendUploadParseOptions(formData, parseOptions);
    const url = `${API_BASE_URL}/projects/${projectId}/self-upload`;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    const res = await fetch(url, { method: "POST", headers, body: formData });
    if (!res.ok) {
      const body: { error?: string; pii_value_findings?: PIIValueFinding[] } =
        await res.json().catch(() => ({}));
      const err = new Error(body.error || "Upload failed") as Error & {
        piiValueFindings?: PIIValueFinding[];
      };
      if (body.pii_value_findings)
        err.piiValueFindings = body.pii_value_findings;
      throw err;
    }
    return res.json();
  }

  // Access Request Methods (Self-Registration with PI Approval)
  async getPendingAccessRequests(): Promise<AccessRequestListResponse> {
    return this.request<AccessRequestListResponse>("/admin/access-requests");
  }

  async approveAccessRequest(
    requestId: number,
    request: ApproveAccessRequestRequest,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/admin/access-requests/${requestId}/approve`,
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
  }

  async rejectAccessRequest(
    requestId: number,
    notes?: string,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/admin/access-requests/${requestId}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ notes }),
      },
    );
  }

  async getMyAccessRequests(): Promise<AccessRequestListResponse> {
    return this.request<AccessRequestListResponse>("/my/access-requests");
  }

  // Invitation Methods
  async createInvitation(
    projectId: number,
    request: CreateInvitationRequest,
  ): Promise<InvitationResponse> {
    return this.request<InvitationResponse>(
      `/admin/projects/${projectId}/invitations`,
      {
        method: "POST",
        body: JSON.stringify(request),
      },
    );
  }

  async validateInvitation(token: string): Promise<ValidateInvitationResponse> {
    return this.request<ValidateInvitationResponse>(
      `/invitations/validate?token=${encodeURIComponent(token)}`,
    );
  }

  async listInvitations(projectId: number): Promise<InvitationListResponse> {
    return this.request<InvitationListResponse>(
      `/admin/projects/${projectId}/invitations`,
    );
  }

  async inviteUploader(
    email: string,
    custom_message?: string,
    organization_id?: number,
    organization_name?: string,
    data_request_id?: number,
    recipient_name?: string,
  ): Promise<InvitationResponse> {
    return this.request<InvitationResponse>("/admin/invite-uploader", {
      method: "POST",
      body: JSON.stringify({
        email,
        custom_message,
        organization_id,
        organization_name,
        data_request_id,
        recipient_name,
      }),
    });
  }

  async revokeInvitation(
    invitationId: number,
  ): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      `/admin/invitations/${invitationId}`,
      {
        method: "DELETE",
      },
    );
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  // Notification methods
  async getNotifications(
    unreadOnly: boolean = false,
    limit: number = 20,
    offset: number = 0,
  ): Promise<NotificationListResponse> {
    const params = new URLSearchParams({
      unread_only: unreadOnly.toString(),
      limit: limit.toString(),
      offset: offset.toString(),
    });
    return this.request<NotificationListResponse>(`/notifications?${params}`);
  }

  async getNotificationUnreadCount(): Promise<NotificationUnreadCountResponse> {
    return this.request<NotificationUnreadCountResponse>(
      "/notifications/unread-count",
    );
  }

  async markNotificationAsRead(notificationId: number): Promise<Notification> {
    return this.request<Notification>(`/notifications/${notificationId}/read`, {
      method: "PUT",
    });
  }

  async markAllNotificationsAsRead(): Promise<{
    success: boolean;
    marked_count: number;
  }> {
    return this.request<{ success: boolean; marked_count: number }>(
      "/notifications/read-all",
      {
        method: "PUT",
      },
    );
  }

  async dismissNotification(notificationId: number): Promise<void> {
    await this.request<void>(`/notifications/${notificationId}`, {
      method: "DELETE",
    });
  }

  async broadcastNotification(payload: {
    title: string;
    message: string;
    audience: "all_org" | "project_members" | "all_pis" | "specific_users";
    project_ids?: number[];
    user_ids?: number[];
  }): Promise<{
    success: boolean;
    recipients: number;
    message: string;
    recipient_list: Array<{ id: number; name: string; email: string }>;
  }> {
    return this.request("/admin/notifications/broadcast", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ── Data Source Portal ──────────────────────────────────────────────────

  async getDataSourceActivity(): Promise<{
    activity: DataSourceActivity[];
    total: number;
  }> {
    return this.request("/data-source/activity");
  }

  async getDataSourceRedirectedRequests(): Promise<{
    requests: DataRequest[];
    total: number;
  }> {
    return this.request("/data-source/redirected");
  }

  async acceptRedirectedRequest(
    requestId: number,
  ): Promise<{ message: string; request: DataRequest }> {
    return this.request(`/data-source/redirected/${requestId}/accept`, {
      method: "POST",
    });
  }

  async declineRedirectedRequest(
    requestId: number,
  ): Promise<{ message: string; request: DataRequest }> {
    return this.request(`/data-source/redirected/${requestId}/decline`, {
      method: "POST",
    });
  }

  async getDataSourceAcceptedRequests(): Promise<{
    requests: DataRequest[];
    total: number;
  }> {
    return this.request("/data-source/accepted");
  }

  async uploadDataForRequest(
    requestId: number,
    file: File,
    datasetTitle?: string,
    datasetExpiry?: string,
    allowCrossProject?: boolean,
    piiFindingsAcknowledged?: boolean,
    piiValueRedactions?: PIIValueFinding[],
    piiDroppedColumns?: string[],
    parseOptions?: UploadParseOptions,
  ): Promise<{ message: string; dataset_id?: number; row_count?: number }> {
    const form = new FormData();
    form.append("file", file);
    if (datasetTitle) form.append("title", datasetTitle);
    if (datasetExpiry) form.append("dataset_expiry", datasetExpiry);
    if (allowCrossProject) form.append("allow_cross_project", "true");
    if (piiFindingsAcknowledged)
      form.append("pii_findings_acknowledged", "true");
    if (piiValueRedactions && piiValueRedactions.length > 0) {
      form.append("pii_value_redactions", JSON.stringify(piiValueRedactions));
    }
    if (piiDroppedColumns && piiDroppedColumns.length > 0) {
      form.append("pii_dropped_columns", JSON.stringify(piiDroppedColumns));
    }
    appendUploadParseOptions(form, parseOptions);

    // We can't use request() because we need to inspect a 422-with-body
    // response to surface PII findings to the caller. The pseudonymizer
    // returns the findings list in the error body so the UI can re-render
    // them without a separate preview call.
    const url = `${API_BASE_URL}/data-source/accepted/${requestId}/upload`;
    const headers: Record<string, string> = {};
    if (this.token) headers.Authorization = `Bearer ${this.token}`;

    const res = await fetch(url, { method: "POST", headers, body: form });
    if (!res.ok) {
      const body: { error?: string; pii_value_findings?: PIIValueFinding[] } =
        await res.json().catch(() => ({}));
      const err = new Error(body.error || "Upload failed") as Error & {
        piiValueFindings?: PIIValueFinding[];
      };
      if (body.pii_value_findings)
        err.piiValueFindings = body.pii_value_findings;
      throw err;
    }
    return res.json();
  }
}

export const apiService = new ApiService();
